"use client";

import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";

import { useNodeViewSelected } from "@/components/ui/editor/lib/use-node-view-selected";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

import type { SnippetBlockAttrs } from "./snippet-extension";

/**
 * React node view for the `snippet-block` ProseMirror node.
 *
 * Renders a card-style block (block-level per Phase E spec). Layout
 * matches the Figma `198:302` snippet pill but expanded to a full-width
 * card so the block can sit between paragraphs without disturbing flow.
 *
 * The card is read-only inside the editor — the snippet's body is
 * resolved server-side at render time. To edit the snippet body itself
 * users must navigate to the snippets feature; clicking the card here
 * opens the snippet picker (Task 40) so the author can swap in a
 * different snippet.
 *
 * `display_title` is read straight off the node attrs (denormalized at
 * insert time). When missing or empty we fall back to a generic
 * "Snippet" label so the card never appears empty.
 */
export function SnippetBlockView(props: NodeViewProps) {
  const { node, editor, deleteNode, getPos, selected } = props;
  const attrs = node.attrs as SnippetBlockAttrs;
  const editable = editor.isEditable;
  const title =
    attrs.display_title && attrs.display_title.trim().length > 0
      ? attrs.display_title
      : "Snippet";
  const isSelected = useNodeViewSelected({ node, editor, getPos, selected });

  return (
    <NodeViewWrapper
      as="div"
      data-snippet-id={attrs.snippet_id}
      data-display-title={attrs.display_title}
      className={cn(
        "my-2 inline-flex w-fit max-w-full items-center gap-2 rounded-md border px-2 py-1",
        "bg-[rgba(124,58,237,0.08)] dark:bg-[rgba(200,179,240,0.06)] border-[rgba(124,58,237,0.2)] dark:border-[rgba(200,179,240,0.15)] text-[#7c3aed] dark:text-[#c8b3f0]",
        "select-none",
        isSelected && "ring-2 ring-primary",
      )}
    >
      <Icon
        name="IconScissors2"
        size={14}
        className="opacity-80"
        aria-hidden="true"
      />
      <span className="truncate text-sm font-medium">{title}</span>
      {editable ? (
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            deleteNode();
          }}
          aria-label={`Remove snippet ${title}`}
          className="ml-1 inline-flex size-3 cursor-pointer items-center justify-center text-[#7c3aed]/70 dark:text-[#c8b3f0]/70 hover:text-[#6d28d9] dark:hover:text-[#c8b3f0]"
        >
          <Icon name="IconX" size={12} />
        </button>
      ) : null}
    </NodeViewWrapper>
  );
}
